import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ServiceCostCalculator.css';
import { loadBusinessSettings, saveBusinessSettings } from '../utils/businessSettingsHelper';

const ServiceCostCalculator = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [service, setService] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [serviceTime, setServiceTime] = useState(0);
  
  // Change the default values to null and load them from business settings
  const [profitMargin, setProfitMargin] = useState(null);
  const [vatRate, setVatRate] = useState(null);
  const [calculatedCosts, setCalculatedCosts] = useState(null);
  const [businessSettings, setBusinessSettings] = useState(null);
  
  // Calculate total product cost
  const calculateTotalProductCost = () => {
    let totalProductCost = 0;
    
    // Loop through selected products
    selectedProducts.forEach(selectedProduct => {
      // Find the full product details
      const product = products.find(p => p.Product_ID === selectedProduct.productId);
      if (product && product.price && product.qty) {
        // Calculate cost using the original formula
        const perUnit = product.price / product.qty;
        const productCost = perUnit * selectedProduct.usage;
        totalProductCost += productCost;
      }
    });
    
    return totalProductCost;
  };
  
  // Fetch business settings and service details on component load
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // First fetch business settings
        const settings = await loadBusinessSettings();
        console.log('Business settings loaded:', settings);
        
        // Check if essential calculation values exist
        const missingSettings = [];
        if (settings.default_profit_margin === undefined || settings.default_profit_margin === null) missingSettings.push('Default Profit Margin');
        if (settings.default_vat_rate === undefined || settings.default_vat_rate === null) missingSettings.push('VAT Rate');
        if (settings.hourly_rate === undefined || settings.hourly_rate === null) missingSettings.push('Hourly Rate');
        if (settings.overhead_per_minute === undefined || settings.overhead_per_minute === null) missingSettings.push('Overhead Per Minute');
        
        if (missingSettings.length > 0) {
          // Critical settings are missing
          setError(`Please configure your business settings before proceeding. Missing values: ${missingSettings.join(', ')}`);
          setLoading(false);
          return; // Don't continue loading the calculator
        }
        
        setBusinessSettings(settings);
        
        // Use business settings for defaults
        if (profitMargin === null) {
          setProfitMargin(settings.default_profit_margin);
        }
        
        if (vatRate === null) {
          setVatRate(settings.default_vat_rate);
        }
        
        // Fetch service details
        const serviceResponse = await fetch(`/api/services/${serviceId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!serviceResponse.ok) {
          throw new Error('Failed to fetch service details');
        }
        
        const serviceData = await serviceResponse.json();
        setService(serviceData);
        setServiceTime(serviceData.Service_Time || 0);
        
        // Fetch all products
        const productsResponse = await fetch('/api/products', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!productsResponse.ok) {
          throw new Error('Failed to fetch products');
        }
        
        const productsData = await productsResponse.json();
        setProducts(productsData);
        
        // Fetch service details (product associations)
        const serviceDetailsResponse = await fetch(`/api/services/${serviceId}/products`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (serviceDetailsResponse.ok) {
          const serviceDetails = await serviceDetailsResponse.json();
          
          // Transform service details into selected products
          const selectedProds = serviceDetails.map(detail => ({
            productId: detail.product_id || productsData.find(p => p.Product_Name === detail.Product_Name)?.Product_ID,
            usage: detail.Usage || 0,
            productName: detail.Product_Name
          })).filter(p => p.productId); // Filter out any without a product ID
          
          setSelectedProducts(selectedProds);
        }
        
        // Fetch any existing calculated costs
        const costsResponse = await fetch(`/api/service-costs/${serviceId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (costsResponse.ok) {
          const costsData = await costsResponse.json();
          setCalculatedCosts(costsData);
          
          // Use any existing profit margin and VAT rate if they exist
          if (costsData.profit_margin) {
            setProfitMargin(costsData.profit_margin);
          }
          
          if (costsData.vat_rate) {
            setVatRate(costsData.vat_rate);
          }
        }
        
      } catch (err) {
        console.error('Error fetching service data:', err);
        setError(err.message || 'Failed to load service data');
      } finally {
        setLoading(false);
      }
    };
    
    if (serviceId) {
      fetchData();
    }
  }, [serviceId, profitMargin, vatRate]);

  // Handle product selection
  const handleProductSelectionChange = (productId, checked) => {
    if (checked) {
      // Add product if not already in the list
      if (!selectedProducts.some(p => p.productId === productId)) {
        const product = products.find(p => p.Product_ID === productId);
        setSelectedProducts([
          ...selectedProducts,
          {
            productId,
            usage: 1, // Default usage
            productName: product.Product_Name
          }
        ]);
      }
    } else {
      // Remove product
      setSelectedProducts(selectedProducts.filter(p => p.productId !== productId));
    }
  };
  
  // Handle usage change
  const handleUsageChange = (productId, usage) => {
    setSelectedProducts(
      selectedProducts.map(p => 
        p.productId === productId 
          ? { ...p, usage: parseFloat(usage) || 0 } 
          : p
      )
    );
  };
  
  // Calculate service costs
  const calculateCosts = async () => {
    try {
      setCalculating(true);
      setError(null);
      
      // First, update service time if changed
      if (service.Service_Time !== serviceTime) {
        await fetch(`/api/services/${serviceId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            time: serviceTime
          })
        });
      }
      
      // Update product associations
      await fetch(`/api/services/${serviceId}/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          products: selectedProducts.map(p => ({
            product_id: p.productId,
            usage: p.usage
          }))
        })
      });
      
      // Calculate costs - use the current profit margin and VAT rate values
      const calculationResponse = await fetch(`/api/service-costs/${serviceId}/calculate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          profit_margin: profitMargin,
          vat_rate: vatRate
        })
      });
      
      if (!calculationResponse.ok) {
        const errorData = await calculationResponse.json();
        throw new Error(errorData.error || 'Failed to calculate costs');
      }
      
      const calculationResult = await calculationResponse.json();
      setCalculatedCosts(calculationResult);
      
    } catch (err) {
      console.error('Error calculating costs:', err);
      setError(err.message || 'Failed to calculate costs');
    } finally {
      setCalculating(false);
    }
  };
  
  // Apply recommended price
  const applyRecommendedPrice = async () => {
    try {
      setCalculating(true);
      
      const response = await fetch(`/api/service-costs/${serviceId}/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to apply recommended price');
      }
      
      // Update service with new price
      setService({
        ...service,
        Current_Cost: calculatedCosts.recommended_price
      });
      
      alert('Recommended price applied successfully!');
      
    } catch (err) {
      console.error('Error applying recommended price:', err);
      setError(err.message || 'Failed to apply recommended price');
    } finally {
      setCalculating(false);
    }
  };
  
  // Render products table with costs
  const renderProductsTable = () => {
    return (
      <div className="products-list">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Selected</th>
              <th>Usage</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => {
              // Check if this product is selected
              const isSelected = selectedProducts.some(p => p.productId === product.Product_ID);
              // Get usage if selected
              const usage = isSelected 
                ? selectedProducts.find(p => p.productId === product.Product_ID)?.usage 
                : 0;
              
              // Calculate per-product cost
              const productCost = isSelected && product.price && product.qty 
                ? (product.price / product.qty) * usage 
                : 0;
                
              return (
                <tr key={product.Product_ID} className={isSelected ? 'selected' : ''}>
                  <td>{product.Product_Name}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleProductSelectionChange(product.Product_ID, e.target.checked)}
                    />
                  </td>
                  <td>
                    {isSelected && (
                      <input
                        type="number"
                        value={usage}
                        onChange={(e) => handleUsageChange(product.Product_ID, e.target.value)}
                        min="0"
                        step="1"
                      />
                    )}
                  </td>
                  <td>
                    {isSelected ? `£${productCost.toFixed(2)}` : '£0.00'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3"><strong>Total Product Cost:</strong></td>
              <td><strong>£{calculateTotalProductCost().toFixed(2)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading service data...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        {error.includes('business settings') && (
          <button
            className="btn-primary"
            onClick={() => navigate('/settings/business')}
          >
            Configure Business Settings
          </button>
        )}
        <button
          className="btn-secondary"
          onClick={() => navigate('/calculator/services')}
        >
          Back to Services
        </button>
      </div>
    );
  }

  if (!service) {
    return <div className="error-message">Service not found</div>;
  }

  return (
    <div className="service-cost-calculator">
      <div className="calculator-header">
        <h2>Calculate Costs for {service.Service_Name}</h2>
        <button
          className="btn-back"
          onClick={() => navigate('/calculator/services')}
        >
          Back to Services
        </button>
      </div>
      // Add this guidance panel to your ServiceCostCalculator component
// Insert this after the calculator-header div and before the calculator-form div

{/* Service Pricing Guidance Panel */}
<div className="calculator-guidance">
  <h3>💡 Service Pricing Guidance</h3>
  <div className="guidance-grid">
    <div className="guidance-item materials">
      <span className="guidance-icon">🧪</span>
      <div className="guidance-content">
        <strong>Cost of Materials:</strong> Select all products used in this service
        <span className="tooltip">ℹ️
          <div className="tooltip-content">
            Include hair color, treatments, shampoo, conditioner, styling products, and any consumables. Set realistic usage amounts based on actual consumption.
          </div>
        </span>
      </div>
    </div>
    <div className="guidance-item duration">
      <span className="guidance-icon">⏱️</span>
      <div className="guidance-content">
        <strong>Service Duration:</strong> Include consultation, service, and styling time
        <span className="tooltip">ℹ️
          <div className="tooltip-content">
            Add time for client consultation (5-15 mins), the actual service, drying/styling, and cleanup between clients. Be realistic about total chair time.
          </div>
        </span>
      </div>
    </div>
    <div className="guidance-item profit">
      <span className="guidance-icon">📈</span>
      <div className="guidance-content">
        <strong>Your Profit Settings:</strong> Using {profitMargin !== null ? profitMargin : (businessSettings?.default_profit_margin || 'default')}% margin from business settings
        <span className="tooltip">ℹ️
          <div className="tooltip-content">
            This is your default profit level ({businessSettings?.default_profit_margin}% standard). You can adjust this for individual services or update your business defaults. Industry standard: 10-15% for sustainable growth.
          </div>
        </span>
      </div>
    </div>
  </div>
  
  {/* Quick Tips */}
  <div className="quick-tips">
    <h4>🎯 Quick Tips:</h4>
    <ul>
      <li><strong>Accurate Costing:</strong> Your overhead costs (£{businessSettings?.overhead_per_minute?.toFixed(4) || '0.00'}/min) are automatically included</li>
      <li><strong>Competitive Pricing:</strong> 10-15% profit margins keep you competitive while profitable</li>
      <li><strong>Time is Money:</strong> Don't underestimate service duration - include all client contact time</li>
    </ul>
  </div>
</div>
      
      <div className="calculator-form">
        <div className="service-info">
          <div className="form-group">
            <label htmlFor="service-name">Service Name:</label>
            <input
              type="text"
              id="service-name"
              value={service.Service_Name}
              disabled
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="service-category">Category:</label>
            <input
              type="text"
              id="service-category"
              value={service.service_cat || 'Uncategorized'}
              disabled
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="service-time">Service Time (minutes):</label>
            <input
              type="number"
              id="service-time"
              value={serviceTime}
              onChange={(e) => setServiceTime(Math.max(0, parseInt(e.target.value) || 0))}
              min="0"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="current-price">Current Price:</label>
            <input
              type="text"
              id="current-price"
              value={`£${service.Current_Cost?.toFixed(2) || '0.00'}`}
              disabled
            />
          </div>
        </div>
        
        <div className="calculation-settings">
          <h3>Calculation Settings</h3>
          
          <div className="form-group">
            <label htmlFor="profit-margin">Profit Margin (%):</label>
            <input
              type="number"
              id="profit-margin"
              value={profitMargin !== null ? profitMargin : (businessSettings ? businessSettings.default_profit_margin : '')}
              onChange={(e) => setProfitMargin(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              min="0"
              max="100"
            />
            {businessSettings && profitMargin !== businessSettings.default_profit_margin && (
              <small className="business-settings-note">
                Business default: {businessSettings.default_profit_margin}%
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="vat-rate">VAT Rate (%):</label>
            <input
              type="number"
              id="vat-rate"
              value={vatRate !== null ? vatRate : (businessSettings ? businessSettings.default_vat_rate : '')}
              onChange={(e) => setVatRate(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              min="0"
              max="100"
            />
            {businessSettings && vatRate !== businessSettings.default_vat_rate && (
              <small className="business-settings-note">
                Business default: {businessSettings.default_vat_rate}%
              </small>
            )}
          </div>
        </div>
        
        <div className="product-selection">
          <h3>Product Usage</h3>
          <p>Select the products used in this service and specify the usage amount:</p>
          
          {renderProductsTable()}
        </div>
        
        <div className="calculation-actions">
          <button
            className="btn-calculate"
            onClick={calculateCosts}
            disabled={calculating}
          >
            {calculating ? 'Calculating...' : 'Calculate Costs'}
          </button>
        </div>
      </div>
      
      {calculatedCosts && (
        <div className="calculation-results">
          <h3>Calculation Results</h3>
          
          <div className="results-grid">
            <div className="result-item">
              <span className="result-label">Product Cost:</span>
              <span className="result-value">£{calculatedCosts.product_cost?.toFixed(2)}</span>
            </div>
            
            <div className="result-item">
              <span className="result-label">Time Cost:</span>
              <span className="result-value">£{calculatedCosts.time_cost?.toFixed(2)}</span>
            </div>
            
            <div className="result-item">
              <span className="result-label">Overhead Cost:</span>
              <span className="result-value">£{calculatedCosts.overhead_cost?.toFixed(2)}</span>
            </div>
            
            <div className="result-item total">
              <span className="result-label">Total Cost:</span>
              <span className="result-value">£{calculatedCosts.total_cost?.toFixed(2)}</span>
            </div>
            
            <div className="result-item highlighted">
              <span className="result-label">Recommended Price (ex VAT):</span>
              <span className="result-value">£{calculatedCosts.recommended_price?.toFixed(2)}</span>
            </div>
            
            <div className="result-item">
              <span className="result-label">Price with VAT:</span>
              <span className="result-value">£{calculatedCosts.price_with_vat?.toFixed(2)}</span>
            </div>
            
            <div className="result-item">
              <span className="result-label">Current Price:</span>
              <span className="result-value">£{service.Current_Cost?.toFixed(2) || '0.00'}</span>
            </div>
            
            <div className="result-item">
              <span className="result-label">Difference:</span>
              <span className={`result-value ${calculatedCosts.recommended_price > service.Current_Cost ? 'positive' : calculatedCosts.recommended_price < service.Current_Cost ? 'negative' : ''}`}>
                {calculatedCosts.recommended_price !== service.Current_Cost 
                  ? `${calculatedCosts.recommended_price > service.Current_Cost ? '+' : ''}£${Math.abs(calculatedCosts.recommended_price - service.Current_Cost).toFixed(2)}` 
                  : '£0.00'}
              </span>
            </div>
          </div>
          
          <div className="results-actions">
            <button
              className="btn-apply"
              onClick={applyRecommendedPrice}
              disabled={calculating}
            >
              {calculating ? 'Applying...' : 'Apply Recommended Price'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCostCalculator;